import { getSearchParams } from "@/api/company";
import { transformFilters } from "@/views/search/components/Filters/filterMapper";

const state = {
  isLoading: false,
  rawFilterData: null,
  rawFilterDataPromise: null,
};

const mutations = {
  setLoading: (state, isLoading) => {
    state.isLoading = isLoading;
  },
  setRawFilterData: (state, data) => {
    state.rawFilterData = data;
    state.rawFilterDataPromise = null;
  },
  setRowFilterDataPromise: (state, promise) => {
    state.rawFilterDataPromise = promise;
  },
};

const actions = {
  async loadAndFormatFilters(
    { dispatch, commit, state },
    { getQueryFromBackend, backendParams = {}, requiredLookups = [] }
  ) {
    commit("setLoading", true);

    const dispatcherMap = {
      sequenceList: () =>
        dispatch("common/getSequenceList", null, { root: true }),
      locationListMap: () =>
        dispatch("common/getLocationList", null, { root: true }),
      techCategoryList: () =>
        dispatch("common/getCategoryList", null, { root: true }),
      newsCategoryList: () =>
        dispatch("common/getNewsCategorysList", null, { root: true }),
      companySavedList: (params) =>
        dispatch("contact/getSavedList", params, { root: true }),
      peopleSavedList: (params) =>
        dispatch("contact/getSavedList", params, { root: true }),
      managementLevels: () =>
        dispatch("common/getManagementLevels", null, { root: true }),
      departments: () =>
        dispatch("common/getDepartments", null, { root: true }),
      departmentFunctions: () =>
        dispatch("common/getDepartmentFunctions", null, { root: true }),
      mediaList: () => dispatch("common/getMediaList", null, { root: true }),
      verticalsCategoryList: () =>
        dispatch("common/getIndustryCategory", null, { root: true }),
      verticalCategoryList: () =>
        dispatch("common/getVerticalCategoryList", null, { root: true }),
      companySize: () =>
        dispatch("common/getCompanySize", null, { root: true }),
    };

    try {
      const allPromises = [];

      if (getQueryFromBackend) {
        allPromises.push(dispatch("getUrlParamsList", backendParams));
      } else {
        allPromises.push(Promise.resolve(state.rawFilterData));
      }

      const lookupPromises = requiredLookups.map((lookup) => {
        const lookupName = typeof lookup === "string" ? lookup : lookup.name;
        const params = typeof lookup === "object" ? lookup.params : null;

        const dispatcher = dispatcherMap[lookupName];
        if (dispatcher) {
          return dispatcher(params);
        }
        console.warn(`Unknown lookup dependency requested: ${lookupName}`);
        return Promise.resolve(null);
      });

      allPromises.push(...lookupPromises);

      const [rawFilterData, ...lookupResults] = await Promise.all(allPromises);

      if (!rawFilterData) {
        console.warn("Cannot format filters because rawFilterData is null.");
        commit("setRawFilterData", null);
        return null;
      }
      const lookups = {};
      requiredLookups.forEach((lookup, index) => {
        const lookupName = typeof lookup === "string" ? lookup : lookup.name;
        lookups[lookupName] = lookupResults[index];
      });

      const finalData = transformFilters(rawFilterData, lookups);

      commit("setRawFilterData", null);
      return finalData;
    } catch (error) {
      console.error("Failed to load and format filters:", error);
    } finally {
      commit("setLoading", false);
    }
  },

  async getUrlParamsList({ commit, rootState }, params) {
    if (state.rawFilterData) {
      return Promise.resolve(state.rawFilterData);
    }
    if (state.rawFilterDataPromise) {
      return state.rawFilterDataPromise;
    }

    const promise = getSearchParams({
      profile_id: rootState.user.currentId.profile_id,
      ...params,
    })
      .then((result) => {
        const originParams = {
          ...result.data.parameters,
          ...result.data.parameter_enrichment,
        };
        commit("setRawFilterData", originParams);
        return originParams;
      })
      .catch((err) => {
        commit("setRowFilterDataPromise", null);
        throw err;
      });

    return promise;
  },

  async getReadyList({ dispatch }, { requiredLookups = [], forceRefresh = false, }) {
    const dispatcherMap = {
      sequenceList: () =>
        dispatch("common/getSequenceList", { forceRefresh }, { root: true }),
      locationListMap: () =>
        dispatch("common/getLocationList", { forceRefresh }, { root: true }),
      techCategoryList: () =>
        dispatch("common/getCategoryList", { forceRefresh }, { root: true }),
      mediaList: () => dispatch("common/getMediaList", { forceRefresh }, { root: true }),
      newsCategoryList: () =>
        dispatch("common/getNewsCategorysList", { forceRefresh }, { root: true }),
      companySavedList: (params) =>
        dispatch("contact/getSavedList", { forceRefresh, ...params }, { root: true }),
      peopleSavedList: (params) =>
        dispatch("contact/getSavedList", { forceRefresh, ...params }, { root: true }),
      managementLevels: () =>
        dispatch("common/getManagementLevels", { forceRefresh }, { root: true }),
      departments: () =>
        dispatch("common/getDepartments", { forceRefresh }, { root: true }),
      departmentFunctions: () =>
        dispatch("common/getDepartmentFunctions", { forceRefresh }, { root: true }),
      adList: () => dispatch("common/getAdList", { forceRefresh }, { root: true }),
      verticalsCategoryList: () =>
        dispatch("common/getIndustryCategory", { forceRefresh }, { root: true }),
      verticalCategoryList: () =>
        dispatch("common/getVerticalCategoryList", { forceRefresh }, { root: true }),
      companySize: () =>
        dispatch("common/getCompanySize", { forceRefresh }, { root: true }),
    };

    try {
      const lookupPromises = requiredLookups.map(async (lookup) => {
        const lookupName = typeof lookup === "string" ? lookup : lookup.name;
        const params = typeof lookup === "object" ? lookup.params : null;

        const dispatcher = dispatcherMap[lookupName];
        if (dispatcher) {
          return dispatcher(params);
        }
        console.warn(`Unknown lookup dependency requested: ${lookupName}`);
        return Promise.resolve(null);
      });

      const lookupResults = await Promise.all(lookupPromises);

      const lookups = {};
      requiredLookups.forEach((lookup, index) => {
        const lookupName = typeof lookup === "string" ? lookup : lookup.name;
        lookups[lookupName] = lookupResults[index];
      });

      return lookups;
    } catch (error) {
      console.error("Failed to load and format filters:", error);
    }
  },

  async formatFilters(
    { dispatch, commit, state },
    { params, requiredLookups = [] }
  ) {
    const dispatcherMap = {
      sequenceList: () =>
        dispatch("common/getSequenceList", null, { root: true }),
      locationListMap: () =>
        dispatch("common/getLocationList", null, { root: true }),
      techCategoryList: () =>
        dispatch("common/getCategoryList", null, { root: true }),
      newsCategoryList: () =>
        dispatch("common/getNewsCategorysList", null, { root: true }),
      companySavedList: (params) =>
        dispatch("contact/getSavedList", params, { root: true }),
      peopleSavedList: (params) =>
        dispatch("contact/getSavedList", params, { root: true }),
      managementLevels: () =>
        dispatch("common/getManagementLevels", null, { root: true }),
      departments: () =>
        dispatch("common/getDepartments", null, { root: true }),
      departmentFunctions: () =>
        dispatch("common/getDepartmentFunctions", null, { root: true }),
      mediaList: () => dispatch("common/getMediaList", null, { root: true }),
      verticalsCategoryList: () =>
        dispatch("common/getIndustryCategory", null, { root: true }),
      verticalCategoryList: () =>
        dispatch("common/getVerticalCategoryList", null, { root: true }),
      companySize: () =>
        dispatch("common/getCompanySize", null, { root: true }),
    };

    try {
      const allPromises = [];
      allPromises.push(Promise.resolve(params));

      const lookupPromises = requiredLookups.map((lookup) => {
        const lookupName = typeof lookup === "string" ? lookup : lookup.name;
        const params = typeof lookup === "object" ? lookup.params : null;

        const dispatcher = dispatcherMap[lookupName];
        if (dispatcher) {
          return dispatcher(params);
        }
        console.warn(`Unknown lookup dependency requested: ${lookupName}`);
        return Promise.resolve(null);
      });

      allPromises.push(...lookupPromises);

      const [rawFilterData, ...lookupResults] = await Promise.all(allPromises);

      if (!rawFilterData) {
        return null;
      }
      const lookups = {};
      requiredLookups.forEach((lookup, index) => {
        const lookupName = typeof lookup === "string" ? lookup : lookup.name;
        lookups[lookupName] = lookupResults[index];
      });

      const finalData = transformFilters(rawFilterData, lookups);
      return finalData;
    } catch (error) {
      console.error("Failed to load and format filters:", error);
    } finally {
      commit("setLoading", false);
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
